// gridAstar — orthogonal pathfinding on a rasterised floor plan.
//
// WHY this exists (root cause of plan A's limits):
//
//   Plan A's pipeline — buildNavGraph + computePath + rectilinearisePolyline —
//   relies on the layout shipping room polygons and corridor centerlines.
//   We use those to anchor graph nodes (room centroid / door hinge /
//   corridor waypoint) and to walk between regions via doors.
//
//   In production we see floor plans where the vision parser emits walls
//   and doors but ZERO rooms / ZERO corridors. The nav-graph collapses
//   to a handful of isolated door nodes with no edges. Snap fails, the
//   path becomes a marker-to-marker straight line, and rectilinearise
//   bends it into an L-shape ONLY when the L-corner's two legs both
//   clear the walls. Many segments fall back to "diagonal that cuts
//   through walls" — exactly what plan A was supposed to prevent.
//
// HOW this fixes it:
//
//   1. rasteriseWalls(layout, cellSize) — bake every wall segment into
//      a boolean grid (true = blocked). Doors carve free cells back out
//      of the wall stamp so the path can walk through them.
//
//   2. astarOrthogonal(grid, start, end) — 4-connected A* with Manhattan
//      heuristic. No diagonals → the output polyline is inherently
//      axis-aligned, satisfying the maintainer's "巡邏路線走直角" requirement
//      without a separate rectilinearisation post-process.
//
//   3. simplifyGridPath(path, cellSize) — collapse collinear runs of
//      cells into endpoint-only polylines, then translate cell indices
//      to image-space pixel points. Output drops straight back into
//      <polyline points="..."> with no further massaging.
//
// Doesn't need rooms or corridors. Works on any layout that has walls.
// Time complexity ~O(N log N) with a binary heap, ~50ms on a 160×120
// grid (640×480 image / 4 px cells) on the median machine.

import type { LayoutSubset } from "./buildGraph.js";
import type { Point } from "./geometry.js";
export type { Point } from "./geometry.js";

/**
 * Pixel side-length of one grid cell. 4 px gives ~38 400 cells on a
 * 640×480 layout — fine for A* on a Worker-class browser. Bumping to
 * 8 px halves the resolution (paths look chunkier in tight corridors).
 * Drop to 2 px and you start to feel the search latency.
 */
export const DEFAULT_CELL_SIZE_PX = 4;

/**
 * How far (in cells) to inflate each wall when stamping it onto the
 * grid. 1 cell is the minimum — without it the A* can squeeze between
 * walls that touch at a corner (the corner cell itself is blocked, but
 * adjacent free cells let the path "tunnel" through the corner).
 *
 * History note (don't bump back to 2): WALL_INFLATE_CELLS=2 with the
 * default 4 px cellSize stamps each wall as an 8-px-wide strip on the
 * grid, which closed the natural door gaps in production layouts where
 * walls + door geometry come from the OpenAI vision parser (typical
 * 38-px door opening minus 8 px per wall edge = 22 px ≈ 5.5 cells, but
 * adjacent perpendicular walls near hinge points stamped on top and
 * sealed every room). Flood-fill from a marker reached only 1 of 13
 * markers on the demo 8F floor; dropping to 1 reaches all 13.
 *
 * If routes ever look like they're hugging walls too tight under this
 * setting, the right fix is to halve cellSize (more grid resolution),
 * not bump inflation back up.
 */
const WALL_INFLATE_CELLS = 1;

/**
 * Up to this many cells in radius we BFS-search for a free cell when a
 * marker happens to land on a blocked cell. Beyond this we give up and
 * report a "no path" result for the segment, letting the caller fall
 * back to whatever it likes (typically: a direct line, ugly but at
 * least visible).
 */
const SNAP_SEARCH_RADIUS_CELLS = 30;

/**
 * Per-turn cost added to the A* edge weight whenever the next move
 * changes direction relative to the arrival direction. On a uniform
 * 4-connected grid every Manhattan-shortest path has the same edge
 * count, so without a turn penalty A* picks an arbitrary staircase
 * pattern. Adding 1.5 cells of cost per turn breaks the tie in favour
 * of L-shaped paths (1 turn) over staircase paths (N turns). The
 * heuristic h = Manhattan distance is still admissible (h ≤ true
 * cost including turns), so A* remains optimal.
 */
const TURN_PENALTY = 1.5;

export interface RasteriseOptions {
  cellSize?: number;
}

export interface RasterGrid {
  /** Grid width in cells. */
  cols: number;
  /** Grid height in cells. */
  rows: number;
  /** Side length of one cell in source-image pixels. */
  cellSize: number;
  /** rows*cols flat array. true = blocked (wall). false = walkable. */
  blocked: Uint8Array;
  /** Same dims as the source image so callers can pass-through. */
  imageSize: { width: number; height: number };
}

/**
 * Build the obstacle grid. Walls get stamped (with inflation); doors
 * carve free cells back out. Pure function, safe to memoise per layout.
 */
export function rasteriseWalls(layout: LayoutSubset, opts: RasteriseOptions = {}): RasterGrid {
  const cellSize = opts.cellSize ?? DEFAULT_CELL_SIZE_PX;
  const W = layout.image_size?.width ?? 640;
  const H = layout.image_size?.height ?? 480;
  const cols = Math.max(1, Math.ceil(W / cellSize));
  const rows = Math.max(1, Math.ceil(H / cellSize));
  const blocked = new Uint8Array(cols * rows);

  // 1. Stamp walls.
  for (const w of layout.walls ?? []) {
    if (!w.points || w.points.length < 2) continue;
    stampSegment(blocked, cols, rows, w.points[0], w.points[1], cellSize, WALL_INFLATE_CELLS);
  }

  // Walls only. Per maintainer ruling, doors are NOT special-cased here:
  // the parser is expected to emit wall segments with natural gaps at
  // door positions, so the A* finds the openings without us having to
  // erode the wall stamp. Treating doors as an extra carve was causing
  // adjacent-wall erosion when a perpendicular wall met a door near its
  // hinge (the disc/ellipse encroached on the perpendicular wall).
  return { cols, rows, cellSize, blocked, imageSize: { width: W, height: H } };
}

export interface AStarResult {
  /** Image-space polyline. Empty if no path was found. */
  polyline: Point[];
  /** Total px length along the polyline; 0 when polyline is empty. */
  totalPx: number;
  /** Human warning ("start blocked", "no path", "snapped start by N cells"). */
  warning: string | null;
}

/**
 * Find an orthogonal (4-connected) shortest path between two image-space
 * points, returning a simplified polyline (collinear runs collapsed).
 *
 * Snap behaviour: if either start or end falls on a blocked cell, we
 * BFS outward (up to SNAP_SEARCH_RADIUS_CELLS) for the nearest free
 * cell. If none is found, we return an empty polyline with a "no path"
 * warning so the caller can decide what to render.
 */
export function astarOrthogonal(grid: RasterGrid, from: Point, to: Point): AStarResult {
  const startRaw = pointToCell(from, grid);
  const endRaw = pointToCell(to, grid);
  const start = snapToFreeCell(grid, startRaw);
  const end = snapToFreeCell(grid, endRaw);
  if (!start || !end) {
    return { polyline: [], totalPx: 0, warning: "起點或終點周邊找不到可走的格" };
  }

  const startIdx = cellIndex(start.col, start.row, grid.cols);
  const endIdx = cellIndex(end.col, end.row, grid.cols);
  if (startIdx === endIdx) {
    const pt = cellCenter(start, grid);
    return { polyline: [pt], totalPx: 0, warning: null };
  }

  // A* with binary-heap open set. gScore stored in a typed Float64Array
  // for cache-friendly lookups; cameFrom is Int32Array.
  const N = grid.cols * grid.rows;
  const INF = Number.POSITIVE_INFINITY;
  const gScore = new Float64Array(N);
  gScore.fill(INF);
  const cameFrom = new Int32Array(N);
  cameFrom.fill(-1);
  gScore[startIdx] = 0;

  const heap = new MinHeap();
  heap.push(startIdx, manhattan(start.col, start.row, end.col, end.row));

  const closed = new Uint8Array(N);
  // 4-neighbours: right, down, left, up.
  const NX = [1, 0, -1, 0];
  const NY = [0, 1, 0, -1];

  // Track which direction we arrived at each cell from. Used to apply a
  // small penalty when the next move would change direction — that
  // breaks the tie between equally-Manhattan paths in favour of fewer
  // turns (one big L instead of a 5-step staircase). Penalty is small
  // enough that an actual detour is still preferred over zigzag, but
  // big enough that staircase vs L isn't a coin-flip.
  const arrivalDir = new Int8Array(N);
  arrivalDir.fill(-1);

  while (heap.size > 0) {
    const cur = heap.pop()!;
    if (cur === endIdx) break;
    if (closed[cur]) continue;
    closed[cur] = 1;
    const cy = (cur / grid.cols) | 0;
    const cx = cur - cy * grid.cols;
    const g = gScore[cur];
    const arrived = arrivalDir[cur];
    for (let k = 0; k < 4; k++) {
      const nx = cx + NX[k];
      const ny = cy + NY[k];
      if (nx < 0 || ny < 0 || nx >= grid.cols || ny >= grid.rows) continue;
      const idx = cellIndex(nx, ny, grid.cols);
      if (closed[idx]) continue;
      if (grid.blocked[idx]) continue;
      const turnCost = arrived >= 0 && arrived !== k ? TURN_PENALTY : 0;
      const tentative = g + 1 + turnCost;
      if (tentative < gScore[idx]) {
        gScore[idx] = tentative;
        cameFrom[idx] = cur;
        arrivalDir[idx] = k;
        heap.push(idx, tentative + manhattan(nx, ny, end.col, end.row));
      }
    }
  }

  if (cameFrom[endIdx] === -1 && startIdx !== endIdx) {
    return { polyline: [], totalPx: 0, warning: "找不到避牆路徑" };
  }

  // Reconstruct cells from end back to start.
  const cells: Array<{ col: number; row: number }> = [];
  let n = endIdx;
  while (n !== -1) {
    const y = (n / grid.cols) | 0;
    const x = n - y * grid.cols;
    cells.push({ col: x, row: y });
    if (n === startIdx) break;
    n = cameFrom[n];
  }
  cells.reverse();

  // Simplify collinear runs.
  const simple = simplifyGridPath(cells);

  // Translate to image-space pixel coords.
  const polyline = simple.map((c) => cellCenter(c, grid));
  // Replace the very first and last point with the exact requested input
  // points so the rendered path begins/ends at the marker pin, not the
  // snapped cell centre — visually cleaner approach lines.
  if (polyline.length > 0) {
    polyline[0] = { ...from };
    polyline[polyline.length - 1] = { ...to };
  }

  let totalPx = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    totalPx += Math.hypot(polyline[i + 1].x - polyline[i].x, polyline[i + 1].y - polyline[i].y);
  }

  const warnings: string[] = [];
  if (startRaw && start && (start.col !== startRaw.col || start.row !== startRaw.row)) {
    warnings.push("起點落在牆上，已就近平移");
  }
  if (endRaw && end && (end.col !== endRaw.col || end.row !== endRaw.row)) {
    warnings.push("終點落在牆上，已就近平移");
  }

  return { polyline, totalPx, warning: warnings.length ? warnings.join("；") : null };
}

// ── helpers ───────────────────────────────────────────────────────────

function cellIndex(col: number, row: number, cols: number): number {
  return row * cols + col;
}

function pointToCell(p: Point, grid: RasterGrid): { col: number; row: number } {
  const col = Math.min(grid.cols - 1, Math.max(0, Math.floor(p.x / grid.cellSize)));
  const row = Math.min(grid.rows - 1, Math.max(0, Math.floor(p.y / grid.cellSize)));
  return { col, row };
}

function cellCenter(c: { col: number; row: number }, grid: RasterGrid): Point {
  return {
    x: c.col * grid.cellSize + grid.cellSize / 2,
    y: c.row * grid.cellSize + grid.cellSize / 2,
  };
}

function manhattan(ax: number, ay: number, bx: number, by: number): number {
  return Math.abs(ax - bx) + Math.abs(ay - by);
}

/**
 * BFS outward from a target cell looking for the nearest non-blocked
 * cell. Returns null if nothing free is found within
 * SNAP_SEARCH_RADIUS_CELLS Chebyshev radius.
 */
function snapToFreeCell(grid: RasterGrid, c: { col: number; row: number }): { col: number; row: number } | null {
  if (!grid.blocked[cellIndex(c.col, c.row, grid.cols)]) return c;
  // Ring-wise expanding search. Could be a proper BFS for big radii but
  // ring search is simpler and fine at radius ≤ ~30.
  for (let r = 1; r <= SNAP_SEARCH_RADIUS_CELLS; r++) {
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const nx = c.col + dx;
        const ny = c.row + dy;
        if (nx < 0 || ny < 0 || nx >= grid.cols || ny >= grid.rows) continue;
        if (!grid.blocked[cellIndex(nx, ny, grid.cols)]) return { col: nx, row: ny };
      }
    }
  }
  return null;
}

/** Drop intermediate cells that lie on a straight horizontal/vertical
 *  segment between their neighbours. Collapses long axis-aligned runs
 *  into endpoint-only polylines. */
function simplifyGridPath(cells: Array<{ col: number; row: number }>): Array<{ col: number; row: number }> {
  if (cells.length < 3) return cells.slice();
  const out = [cells[0]];
  for (let i = 1; i < cells.length - 1; i++) {
    const prev = out[out.length - 1];
    const curr = cells[i];
    const next = cells[i + 1];
    const dx1 = curr.col - prev.col;
    const dy1 = curr.row - prev.row;
    const dx2 = next.col - curr.col;
    const dy2 = next.row - curr.row;
    // Same direction along the same axis? skip the intermediate cell.
    const sameAxis = (dx1 === 0 && dx2 === 0) || (dy1 === 0 && dy2 === 0);
    if (sameAxis && Math.sign(dx1 + dy1) === Math.sign(dx2 + dy2)) {
      continue;
    }
    out.push(curr);
  }
  out.push(cells[cells.length - 1]);
  return out;
}

/** Stamp a thick line segment onto the blocked grid using a supercover
 *  rasteriser (every cell the segment crosses gets blocked, then inflated
 *  by `inflateCells` cells around each touched cell). */
function stampSegment(
  blocked: Uint8Array,
  cols: number,
  rows: number,
  p1: Point,
  p2: Point,
  cellSize: number,
  inflateCells: number,
): void {
  const x1 = p1.x / cellSize;
  const y1 = p1.y / cellSize;
  const x2 = p2.x / cellSize;
  const y2 = p2.y / cellSize;
  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);
  const steps = Math.max(1, Math.ceil(Math.max(dx, dy) * 2));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const cx = Math.floor(x1 + (x2 - x1) * t);
    const cy = Math.floor(y1 + (y2 - y1) * t);
    inflateAt(blocked, cols, rows, cx, cy, inflateCells);
  }
}

function inflateAt(blocked: Uint8Array, cols: number, rows: number, cx: number, cy: number, r: number): void {
  for (let dx = -r; dx <= r; dx++) {
    for (let dy = -r; dy <= r; dy++) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      blocked[ny * cols + nx] = 1;
    }
  }
}

// ── min-heap (binary heap) for the A* open set ────────────────────────

class MinHeap {
  private items: number[] = [];   // cell index
  private priorities: number[] = [];

  get size(): number {
    return this.items.length;
  }

  push(idx: number, priority: number): void {
    this.items.push(idx);
    this.priorities.push(priority);
    this.siftUp(this.items.length - 1);
  }

  pop(): number | undefined {
    if (this.items.length === 0) return undefined;
    const top = this.items[0];
    const lastItem = this.items.pop()!;
    const lastPri = this.priorities.pop()!;
    if (this.items.length > 0) {
      this.items[0] = lastItem;
      this.priorities[0] = lastPri;
      this.siftDown(0);
    }
    return top;
  }

  private siftUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.priorities[i] < this.priorities[parent]) {
        this.swap(i, parent);
        i = parent;
      } else break;
    }
  }

  private siftDown(i: number): void {
    const n = this.items.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let smallest = i;
      if (l < n && this.priorities[l] < this.priorities[smallest]) smallest = l;
      if (r < n && this.priorities[r] < this.priorities[smallest]) smallest = r;
      if (smallest !== i) {
        this.swap(i, smallest);
        i = smallest;
      } else break;
    }
  }

  private swap(a: number, b: number): void {
    [this.items[a], this.items[b]] = [this.items[b], this.items[a]];
    [this.priorities[a], this.priorities[b]] = [this.priorities[b], this.priorities[a]];
  }
}
